import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { API_URL } from "../config.js";
import { showToast } from "../components/toast";

const CvlKycVerifyModal = ({
  isOpen,
  onClose,
  subscriberId,
  initialName = "",
  initialPan = "",
  initialDob = "",
  initialMobile = "",
  onSuccess,
}) => {
  const [pan, setPan] = useState("");
  const [dob, setDob] = useState("");
  const [mobile, setMobile] = useState("");

  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyError, setVerifyError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPan((initialPan || "").toUpperCase());

      let formattedInitialDob = "";
      if (initialDob) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(initialDob)) {
          formattedInitialDob = initialDob;
        } else if (/^\d{2}-\d{2}-\d{4}$/.test(initialDob)) {
          const [d, m, y] = initialDob.split("-");
          formattedInitialDob = `${y}-${m}-${d}`;
        } else if (moment(initialDob).isValid()) {
          formattedInitialDob = moment(initialDob).format("YYYY-MM-DD");
        } else {
          formattedInitialDob = initialDob;
        }
      }
      setDob(formattedInitialDob);

      const cleanMobile = String(initialMobile || "").replace(/\D/g, "").slice(-10);
      setMobile(cleanMobile);

      setVerifyResult(null);
      setVerifyError("");
      setIsSaved(false);
    }
  }, [isOpen, initialName, initialPan, initialDob, initialMobile]);

  if (!isOpen) return null;

  const getFormattedDob = (inputDob) => {
    if (!inputDob) return "";
    const clean = inputDob.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
      const [y, m, d] = clean.split("-");
      return `${d}-${m}-${y}`;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(clean)) return clean;
    const m = moment(clean, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"]);
    return m.isValid() ? m.format("DD-MM-YYYY") : clean;
  };

  // Direct Save & Verify Subscriber
  const handleDirectSave = async (customResult) => {
    const targetResult = customResult || verifyResult;

    if (!targetResult || targetResult.isAllMatched === false || targetResult.kraError) {
      showToast.error("Cannot save: KYC verification failed or mismatched.");
      return;
    }

    const cleanPan = pan.trim().toUpperCase();
    const formattedDob = getFormattedDob(dob);
    const cleanMobile = String(mobile || "").replace(/\D/g, "").slice(-10);

    try {
      setIsSaving(true);
      const token = localStorage.getItem("authToken");

      const cvlName = targetResult?.details?.recordName || targetResult?.rawKycData?.APP_NAME || "";

      const payload = {
        subscriberId: subscriberId,
        mobile: cleanMobile,
        pan: cleanPan,
        recordName: cvlName,
        recordDob: targetResult?.details?.recordDob || formattedDob,
        kycRawData: targetResult?.rawKycData || null,
      };

      const response = await axios.post(`${API_URL}cvl-kyc/direct-verify`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.success) {
        setIsSaved(true);
        showToast.success("KYC Verified & updated successfully!");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        showToast.error(response.data?.msg || "Failed to update subscriber KYC");
      }
    } catch (err) {
      showToast.error(
        err.response?.data?.message || err.response?.data?.msg || "Failed to save KYC"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Step 1: Verify PAN and Directly Save
  const handleVerifyPan = async (e) => {
    if (e) e.preventDefault();
    setVerifyError("");
    setVerifyResult(null);
    setIsSaved(false);

    const cleanPan = pan.trim().toUpperCase();
    const formattedDob = getFormattedDob(dob);

    if (!cleanPan || cleanPan.length !== 10) {
      setVerifyError("Please enter a valid 10-digit PAN number.");
      return;
    }
    if (!formattedDob) {
      setVerifyError("Please enter Date of Birth (DD-MM-YYYY).");
      return;
    }

    try {
      setIsVerifying(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.post(
        `${API_URL}cvl-kyc/verify-pan`,
        {
          pan: cleanPan,
          dob: formattedDob,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        const result = response.data.data;

        // Guard: Stop immediately if KRA returned an error or details mismatched
        if (!result || result.isAllMatched === false || result.kraError) {
          const errMsg =
            result?.fieldErrors?.dob ||
            result?.fieldErrors?.pan ||
            result?.kraError?.desc ||
            "Verification failed: PAN or Date of Birth does not match KRA records.";
          setVerifyError(errMsg);
          showToast.error(errMsg);
          return;
        }

        setVerifyResult(result);

        if (result.cleanMobile && result.cleanMobile.length === 10) {
          setMobile(result.cleanMobile);
        }

        // Direct verification without OTP
        await handleDirectSave(result);
      } else {
        const msg = response.data?.msg || "Failed to verify PAN with KRA";
        setVerifyError(msg);
        showToast.error(msg);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.message ||
        "Failed to verify PAN";
      setVerifyError(msg);
      showToast.error(msg);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div
      id="cvlKycVerifyModal"
      className="modal fade show"
      style={{ display: "block" }}
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <div className="col-md-11">
              <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                Edit & Verify KYC Details
              </h5>
            </div>
            <button type="button" className="btn btn-default float-end" onClick={onClose}>
              &times;
            </button>
            <div className="clearfix"></div>
          </div>

          <form onSubmit={handleVerifyPan}>
            <div className="modal-body">
              <div className="form-group mb-2">
                <label style={{ fontWeight: 600 }}>
                  PAN Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  placeholder="ABCDE1234F"
                  maxLength={10}
                  value={pan}
                  onChange={(e) => setPan(e.target.value.toUpperCase().trim())}
                  required
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontWeight: 600 }}>
                  Date of Birth (DOB) <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  max={moment().format("YYYY-MM-DD")}
                  required
                />
              </div>

            {verifyError && (
              <div className="alert alert-danger py-2 mt-2 mb-2 small">
                {verifyError}
              </div>
            )}

            {/* Verification Result UI commented out as requested */}
            {/*
            {verifyResult && (
              <div className="border rounded p-2 mt-2 bg-light">
                <table className="table table-bordered table-sm bg-white mb-2" style={{ fontSize: "13px" }}>
                  <tbody>
                    <tr>
                      <td style={{ width: "40%" }}>KRA Name (from CVL)</td>
                      <td>
                        <b className="text-primary">{verifyResult.details?.recordName || "N/A"}</b>
                        {verifyResult.details?.recordName && (
                          <span className="badge bg-success ms-2">Updated from CVL</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>KRA DOB</td>
                      <td><b>{verifyResult.details?.recordDob || "N/A"}</b></td>
                    </tr>
                    <tr>
                      <td>PAN Status</td>
                      <td>
                        {verifyResult.panMatched ? (
                          <span className="text-success">Matched</span>
                        ) : (
                          <span className="text-danger">Mismatch</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>DOB Status</td>
                      <td>
                        {verifyResult.dobMatched ? (
                          <span className="text-success">Matched</span>
                        ) : (
                          <span className="text-danger">Mismatch</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {isSaved ? (
                  <div className="alert alert-success py-2 mt-3 mb-0 small text-center">
                    <b>✓ KYC Verified and Updated in Database</b>
                  </div>
                ) : (
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-success btn-sm w-100"
                      onClick={() => handleDirectSave()}
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving..." : "Confirm & Save KYC"}
                    </button>
                  </div>
                )}
              </div>
            )}
            */}
          </div>

          <div className="modal-footer">
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={isVerifying || isSaving}
            >
              {isVerifying || isSaving ? "Verifying & Saving..." : "Verify & Save KYC"}
            </button>
            <button
              type="button"
              className="btn btn-default btn-sm ms-2"
              onClick={onClose}
              disabled={isVerifying || isSaving}
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default CvlKycVerifyModal;
